import type { SupabaseClient } from '../../lib/supabase';
import type {
  CreateSubtaskInput,
  CreateTaskInput,
  ProjectTasks,
  Task,
  TaskActivityEvent,
  TaskActivityWithActor,
  TaskCollaboratorWithProfile,
  TaskUpdate,
} from './types';
import { groupTasks } from './types';

/**
 * Database access for tasks, subtasks, followers and activity history.
 *
 * Every read below is already filtered by Row Level Security and nothing here
 * is trusted for authorization. No query runs at application startup, and
 * activity history is always a separate request (handoff 39).
 */

// One literal, deliberately unwrapped: PostgREST infers the row type from the
// exact string, and concatenating it collapses that inference to unknown.
// prettier-ignore
const TASK_COLUMNS = 'id, project_id, deal_id, lead_id, company_id, prospect_id, task_type, linked_social_post_id, parent_task_id, section_id, status_id, title, description, remarks, assignee_id, priority, due_date, assigned_at, is_completed, completed_at, completed_by, position, created_by, created_at, updated_at, cg_gate_order_id, cg_gate_slug, my_day_section, my_day_position, my_day_planned_on, my_day_section_id, console_group';

function fail(message: string): never {
  throw new Error(message);
}

/**
 * Every task in one project, including subtasks, grouped for the List View.
 * Completed tasks are included: completion is a display state, not a filter.
 */
export async function fetchProjectTasks(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectTasks> {
  const { data, error } = await client
    .from('tasks')
    .select(TASK_COLUMNS)
    .eq('project_id', projectId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return groupTasks(data ?? []);
}

/**
 * One task. Authorization is resolved by this request rather than by a
 * previously loaded list, so an unauthorized id returns null.
 */
export async function fetchTask(client: SupabaseClient, taskId: string): Promise<Task | null> {
  const { data, error } = await client
    .from('tasks')
    .select(TASK_COLUMNS)
    .eq('id', taskId)
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  return data;
}

export async function createTask(
  client: SupabaseClient,
  input: CreateTaskInput & { createdBy: string },
): Promise<Task> {
  const { data, error } = await client
    .from('tasks')
    .insert({
      project_id: input.projectId,
      title: input.title.trim(),
      section_id: input.sectionId,
      status_id: input.statusId,
      assignee_id: input.assigneeId,
      priority: input.priority,
      due_date: input.dueDate,
      position: input.position,
      created_by: input.createdBy,
      // Conditional spread, not `?? null`: with exactOptionalPropertyTypes a
      // key that is absent and a key that is undefined are different things,
      // and the row default (null) is what "not on My Day" means.
      ...(input.myDay === undefined
        ? {}
        : {
            my_day_section: input.myDay.section,
            my_day_section_id: input.myDay.sectionId,
            my_day_planned_on: input.myDay.plannedOn,
            ...(input.myDay.position === undefined
              ? {}
              : { my_day_position: input.myDay.position }),
          }),
    })
    .select(TASK_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * A subtask is a task row with parent_task_id set. Section is deliberately
 * omitted: the database refuses a subtask that also names a section.
 */
export async function createSubtask(
  client: SupabaseClient,
  input: CreateSubtaskInput & { createdBy: string },
): Promise<Task> {
  const { data, error } = await client
    .from('tasks')
    .insert({
      project_id: input.projectId,
      parent_task_id: input.parentTaskId,
      title: input.title.trim(),
      assignee_id: input.assigneeId,
      due_date: input.dueDate,
      created_by: input.createdBy,
    })
    .select(TASK_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

/** An edit restricted to the granted columns. */
export async function updateTask(
  client: SupabaseClient,
  taskId: string,
  changes: TaskUpdate,
): Promise<Task> {
  const { data, error } = await client
    .from('tasks')
    .update(changes)
    .eq('id', taskId)
    .select(TASK_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * Completion and status are independent by design: this never touches
 * status_id, and setTaskStatus never touches is_completed. completed_at and
 * completed_by are stamped by the database.
 */
export async function setTaskCompleted(
  client: SupabaseClient,
  taskId: string,
  isCompleted: boolean,
): Promise<Task> {
  return updateTask(client, taskId, { is_completed: isCompleted });
}

/** Moving a task to a completed-type status does not complete the task. */
export async function setTaskStatus(
  client: SupabaseClient,
  taskId: string,
  statusId: string | null,
): Promise<Task> {
  return updateTask(client, taskId, { status_id: statusId });
}

/**
 * Applies a set of position changes. Two rows swap in the common case, so the
 * updates are sent in sequence and the first failure stops the rest rather
 * than leaving a half-applied order behind quietly.
 */
export async function reorderTasks(
  client: SupabaseClient,
  updates: { id: string; position: number }[],
): Promise<void> {
  for (const update of updates) {
    const { error } = await client
      .from('tasks')
      .update({ position: update.position })
      .eq('id', update.id);

    if (error) {
      fail(error.message);
    }
  }
}

/** Deletion is open to any project editor; completion is the soft path. */
export async function deleteTask(client: SupabaseClient, taskId: string): Promise<void> {
  const { error } = await client.from('tasks').delete().eq('id', taskId);
  if (error) {
    fail(error.message);
  }
}

interface ActivityRow {
  id: string;
  event_type: TaskActivityEvent;
  created_at: string;
  profiles: { display_name: string; email: string } | null;
}

/**
 * Activity history, newest first, joined to each actor's readable identity.
 * Loaded on demand, never with the list. actor_id is set null when a profile
 * is deleted, so the join can come back empty for old entries.
 */
export async function fetchTaskActivity(
  client: SupabaseClient,
  taskId: string,
): Promise<TaskActivityWithActor[]> {
  const { data, error } = await client
    .from('task_activity')
    .select('id, event_type, created_at, profiles:actor_id (display_name, email)')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });

  if (error) {
    fail(error.message);
  }

  return ((data ?? []) as unknown as ActivityRow[]).map((row) => ({
    id: row.id,
    event_type: row.event_type,
    actor_name: row.profiles
      ? row.profiles.display_name.trim() !== ''
        ? row.profiles.display_name
        : row.profiles.email
      : '',
    created_at: row.created_at,
  }));
}

interface CollaboratorRow {
  id: string;
  user_id: string;
  profiles: { display_name: string; email: string } | null;
}

export async function fetchTaskCollaborators(
  client: SupabaseClient,
  taskId: string,
): Promise<TaskCollaboratorWithProfile[]> {
  const { data, error } = await client
    .from('task_collaborators')
    .select('id, user_id, profiles:user_id (display_name, email)')
    .eq('task_id', taskId);

  if (error) {
    fail(error.message);
  }

  return ((data ?? []) as unknown as CollaboratorRow[]).map((row) => ({
    id: row.id,
    user_id: row.user_id,
    display_name: row.profiles?.display_name ?? '',
    email: row.profiles?.email ?? '',
  }));
}

export async function addTaskCollaborator(
  client: SupabaseClient,
  input: { taskId: string; userId: string; addedBy: string },
): Promise<void> {
  const { error } = await client.from('task_collaborators').insert({
    task_id: input.taskId,
    user_id: input.userId,
    added_by: input.addedBy,
  });

  if (error) {
    fail(error.message);
  }
}

export async function removeTaskCollaborator(
  client: SupabaseClient,
  collaboratorId: string,
): Promise<void> {
  const { error } = await client.from('task_collaborators').delete().eq('id', collaboratorId);
  if (error) {
    fail(error.message);
  }
}

/**
 * Database refusals worth explaining. Every message below is raised by a
 * trigger, so the wording is stable and the rule lives in one place.
 */
export const TASK_ERROR_MESSAGES: Record<string, string> = {
  subtask_nesting_not_supported: 'Subtasks cannot have their own subtasks.',
  task_with_subtasks_cannot_be_nested: 'A task with subtasks cannot become a subtask.',
  subtask_project_must_match_parent: 'A subtask must stay in its parent’s project.',
  task_section_must_belong_to_project: 'That section belongs to a different project.',
  task_status_must_belong_to_project: 'That status belongs to a different project.',
  task_assignee_must_be_active_tap_user: 'That person cannot be assigned work.',
  task_assignee_must_be_project_member: 'Add the person to the project before assigning them.',
  task_project_change_not_supported: 'A task cannot be moved to another project.',
  task_reparenting_not_supported: 'A subtask cannot be moved to another task.',
  collaborator_must_be_active_tap_user: 'That person cannot follow this task.',
  collaborator_must_be_project_member: 'Add the person to the project before adding them here.',
  task_activity_is_append_only: 'Activity history cannot be edited.',
  task_due_date_required:
    'This workspace requires a due date on every task. Set one, or ask a TAP admin to turn the rule off in Settings.',
};

/** Turns a database error message into something a person can act on. */
export function taskErrorMessage(message: string): string {
  const known = TASK_ERROR_MESSAGES[message];
  if (known !== undefined) {
    return known;
  }
  // Substring, because PostgREST decorates what Postgres raised. An exact-key
  // lookup never matches the message that actually arrives over the wire, so
  // every real refusal fell through to the generic sentence below.
  for (const [code, sentence] of Object.entries(TASK_ERROR_MESSAGES)) {
    if (message.includes(code)) {
      return sentence;
    }
  }
  // Shown rather than hidden. "That change could not be saved. Try again."
  // sent people round the same loop with nothing new to try; an unfamiliar
  // message they can paste into a chat is worth more than a tidy dead end.
  return message;
}
